import { useLingui } from "@lingui/react/macro";
import { BaseInput, Button } from "@offstreetparking/design-system/forms";
import { Text } from "@offstreetparking/design-system/typography";
import { classNames } from "@offstreetparking/design-system/utils";
import { Duration } from "luxon";
import { useMemo, useState } from "react";
const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
const QuickSelectKey = {
	thirtyMin: "thirtyMin",
	oneHour: "oneHour",
	twoHours: "twoHours",
	max: "max",
} as const;
type QuickSelectKey = (typeof QuickSelectKey)[keyof typeof QuickSelectKey];
const QUICK_SELECT_MINUTES = {
	[QuickSelectKey.thirtyMin]: 30,
	[QuickSelectKey.oneHour]: MINUTES_PER_HOUR,
	[QuickSelectKey.twoHours]: 2 * MINUTES_PER_HOUR,
} as const;
const getSelectedQuickSelectKey = (value: number, maxMinutes?: number): QuickSelectKey | null => {
	if (value === QUICK_SELECT_MINUTES[QuickSelectKey.thirtyMin]) {
		return QuickSelectKey.thirtyMin;
	}
	if (value === QUICK_SELECT_MINUTES[QuickSelectKey.oneHour]) {
		return QuickSelectKey.oneHour;
	}
	if (value === QUICK_SELECT_MINUTES[QuickSelectKey.twoHours]) {
		return QuickSelectKey.twoHours;
	}
	if (maxMinutes != null && value === maxMinutes) {
		return QuickSelectKey.max;
	}
	return null;
};
type DurationParts = {
	days: number;
	hours: number;
	minutes: number;
};
type DurationField = keyof DurationParts;
const toDurationParts = (totalMinutes: number): DurationParts => {
	const d = Duration.fromObject({
		minutes: totalMinutes,
	}).shiftTo("days", "hours", "minutes");
	return {
		days: d.days,
		hours: d.hours,
		minutes: d.minutes,
	};
};
const toMinutes = (d: DurationParts) => {
	return Duration.fromObject(d).as("minutes");
};
type DurationSelectionProps = {
	minutes: number;
	minMinutes?: number;
	maxMinutes?: number;
	onChange: (totalMinutes: number) => void;
};
export const DurationSelection = ({ minutes: value, minMinutes, maxMinutes, onChange }: DurationSelectionProps) => {
	const { t } = useLingui();
	const duration = useMemo(() => toDurationParts(value), [value]);
	const [editingField, setEditingField] = useState<{
		field: DurationField;
		raw: string;
	} | null>(null);
	const selectedQuickSelectKey = editingField ? null : getSelectedQuickSelectKey(value, maxMinutes);
	const handleChange = (d: DurationParts) => {
		onChange(toMinutes(d));
	};
	const getDisplayValue = (field: DurationField) => {
		if (editingField?.field === field) {
			return editingField.raw;
		}
		return duration[field] || "";
	};
	const handleInputChange = (field: DurationField, rawValue: string) => {
		setEditingField({
			field,
			raw: rawValue,
		});
	};
	const handleBlur = () => {
		if (editingField) {
			const parsed = Number(editingField.raw) || 0;
			const next = {
				...duration,
				[editingField.field]: parsed,
			};
			onChange(toMinutes(next));
		}
		setEditingField(null);
	};
	return (
		<div className="space-y-4">
			<div className="flex gap-4 *:flex-1">
				<BaseInput
					name="days"
					value={getDisplayValue("days")}
					onChange={(e) => handleInputChange("days", e.target.value)}
					onBlur={handleBlur}
					placeholder="0"
					disabled={!!maxMinutes && maxMinutes < MINUTES_PER_DAY}
					type="number"
					min="0"
					max={maxMinutes ? Math.floor(maxMinutes / MINUTES_PER_DAY) : undefined}
					rightAddOn={t`days`}
				/>
				<BaseInput
					name="hours"
					value={getDisplayValue("hours")}
					onChange={(e) => handleInputChange("hours", e.target.value)}
					onBlur={handleBlur}
					placeholder="0"
					disabled={!!maxMinutes && maxMinutes < MINUTES_PER_HOUR}
					type="number"
					min="0"
					max={
						maxMinutes
							? Math.floor(
									Math.max(
										0,
										maxMinutes -
											toMinutes({
												...duration,
												hours: 0,
												minutes: 0,
											}),
									) / MINUTES_PER_HOUR,
								)
							: undefined
					}
					rightAddOn={t`hrs`}
				/>
				<BaseInput
					name="minutes"
					value={getDisplayValue("minutes")}
					onChange={(e) => handleInputChange("minutes", e.target.value)}
					onBlur={handleBlur}
					placeholder="0"
					type="number"
					min="0"
					max={
						maxMinutes
							? Math.max(
									0,
									maxMinutes -
										toMinutes({
											...duration,
											minutes: 0,
										}),
								)
							: undefined
					}
					rightAddOn={t`mins`}
				/>
			</div>

			<div>
				<Text size="sm" weight="bold" className="mb-2 block">
					{t`Quick Select`}
				</Text>
				<div className="flex gap-2">
					<Button
						type="button"
						intent="secondary"
						aria-pressed={selectedQuickSelectKey === QuickSelectKey.thirtyMin}
						className={classNames(
							"flex-1",
							selectedQuickSelectKey === QuickSelectKey.thirtyMin
								? "border-primary bg-primary/10 ring-2 ring-primary ring-offset-[-1px]"
								: undefined,
						)}
						onClick={() => handleChange(toDurationParts(QUICK_SELECT_MINUTES[QuickSelectKey.thirtyMin]))}
						isDisabled={
							(!!minMinutes && minMinutes > QUICK_SELECT_MINUTES[QuickSelectKey.thirtyMin]) ||
							(maxMinutes != null && maxMinutes < QUICK_SELECT_MINUTES[QuickSelectKey.thirtyMin])
						}
					>
						{t`30 mins`}
					</Button>
					<Button
						type="button"
						intent="secondary"
						aria-pressed={selectedQuickSelectKey === QuickSelectKey.oneHour}
						className={classNames(
							"flex-1",
							selectedQuickSelectKey === QuickSelectKey.oneHour
								? "border-primary bg-primary/10 ring-2 ring-primary ring-offset-[-1px]"
								: undefined,
						)}
						onClick={() => handleChange(toDurationParts(QUICK_SELECT_MINUTES[QuickSelectKey.oneHour]))}
						isDisabled={
							(!!minMinutes && minMinutes > QUICK_SELECT_MINUTES[QuickSelectKey.oneHour]) ||
							(maxMinutes != null && maxMinutes < QUICK_SELECT_MINUTES[QuickSelectKey.oneHour])
						}
					>
						{t`1 hour`}
					</Button>
					<Button
						type="button"
						intent="secondary"
						aria-pressed={selectedQuickSelectKey === QuickSelectKey.twoHours}
						className={classNames(
							"flex-1",
							selectedQuickSelectKey === QuickSelectKey.twoHours
								? "border-primary bg-primary/10 ring-2 ring-primary ring-offset-[-1px]"
								: undefined,
						)}
						onClick={() => handleChange(toDurationParts(QUICK_SELECT_MINUTES[QuickSelectKey.twoHours]))}
						isDisabled={
							(!!minMinutes && minMinutes > QUICK_SELECT_MINUTES[QuickSelectKey.twoHours]) ||
							(maxMinutes != null && maxMinutes < QUICK_SELECT_MINUTES[QuickSelectKey.twoHours])
						}
					>
						{t`2 hours`}
					</Button>
					<Button
						type="button"
						intent="secondary"
						aria-pressed={selectedQuickSelectKey === QuickSelectKey.max}
						className={classNames(
							"flex-1",
							selectedQuickSelectKey === QuickSelectKey.max
								? "border-primary bg-primary/10 ring-2 ring-primary ring-offset-[-1px]"
								: undefined,
						)}
						onClick={() =>
							handleChange({
								days: 0,
								hours: 0,
								minutes: maxMinutes || 0,
							})
						}
						isDisabled={!maxMinutes}
					>
						{t`Max`}
					</Button>
				</div>
			</div>
		</div>
	);
};
